import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Skeleton } from './ui/skeleton';
import { toast } from 'sonner';
import { getImportantMemories } from '../services/memoryCategoriesService';
import CategoryBadge from './memory/CategoryBadge';
import MilestoneMatchCard from './memory/MilestoneMatchCard';
import { 
  Star, 
  AlertCircle,
  TrendingUp,
  Database
} from 'lucide-react';
import { format } from 'date-fns';

const ImportantMemories = () => {
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    domain: null,
    severity: null,
    min_score: 0.7,
    limit: 50
  });

  useEffect(() => {
    fetchImportantMemories();
  }, [filters]);

  const fetchImportantMemories = async () => {
    setLoading(true);
    try {
      const result = await getImportantMemories(filters);
      setMemories(result.memories || []);
    } catch (error) {
      console.error('Failed to fetch important memories:', error);
      toast.error('Failed to load important memories');
    } finally {
      setLoading(false);
    }
  };

  const domains = [
    { value: 'all', label: 'All Domains' },
    { value: 'communications', label: 'Communications' },
    { value: 'gross_motor', label: 'Gross Motor' },
    { value: 'fine_motor', label: 'Fine Motor' },
    { value: 'cognitive', label: 'Cognitive' },
    { value: 'personal_social', label: 'Personal/Social' },
    { value: 'general', label: 'General' }
  ];

  const severities = [
    { value: 'all', label: 'All Severities' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' },
    { value: 'none', label: 'None' }
  ];

  const renderMemory = (memory) => {
    const categories = memory.categories || {};
    const matchedMilestones = categories.matched_milestones || [];
    const importanceScore = memory.importance_score || 0;
    const isHighSeverity = categories.severity === 'high';
    const hasConcern = categories.secondary_tags?.includes('concern');

    return (
      <Card key={memory.id} className={`mb-4 ${isHighSeverity ? 'border-red-300 bg-red-50/30' : ''}`}>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <CategoryBadge 
                  domain={categories.primary} 
                  severity={categories.severity}
                />
                <div className="flex items-center gap-1 text-sm font-semibold text-emerald-600">
                  <Star className="h-4 w-4 fill-emerald-600" />
                  {(importanceScore * 100).toFixed(0)}%
                </div>
                {hasConcern && (
                  <div className="flex items-center gap-1 text-xs text-red-600">
                    <AlertCircle className="h-3 w-3" />
                    Concern
                  </div>
                )}
              </div>
              <CardDescription className="text-xs">
                {format(new Date(memory.created_at), 'PPp')}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm mb-3">{memory.content}</p>
          
          {matchedMilestones.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-medium text-gray-600 mb-2">
                Matched Milestones ({matchedMilestones.length}):
              </p>
              {matchedMilestones.map((milestone, idx) => (
                <MilestoneMatchCard key={idx} milestone={milestone} />
              ))}
            </div>
          )}

          {categories.secondary_tags && categories.secondary_tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {categories.secondary_tags.map((tag, idx) => (
                <span 
                  key={idx}
                  className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {categories.reasoning && (
            <div className="mt-3 p-2 bg-blue-50 rounded text-xs text-gray-700">
              <p className="font-medium mb-1">Why this is important:</p>
              <p>{categories.reasoning}</p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Star className="h-8 w-8 text-emerald-600 fill-emerald-600" />
          <h1 className="text-3xl font-bold">Important Memories</h1>
        </div>
        <p className="text-gray-600">
          High-priority memories with milestone matches, concerns, or high importance scores
        </p>
      </div>

      <div className="mb-6 flex gap-4">
        <div className="flex-1">
          <label className="text-sm font-medium mb-2 block">Domain</label>
          <Select
            value={filters.domain || 'all'}
            onValueChange={(value) => setFilters({ ...filters, domain: value === 'all' ? null : value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {domains.map((domain) => (
                <SelectItem key={domain.value} value={domain.value}>
                  {domain.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1">
          <label className="text-sm font-medium mb-2 block">Severity</label>
          <Select
            value={filters.severity || 'all'}
            onValueChange={(value) => setFilters({ ...filters, severity: value === 'all' ? null : value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {severities.map((severity) => (
                <SelectItem key={severity.value} value={severity.value}>
                  {severity.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-end">
          <Button onClick={fetchImportantMemories}>
            <TrendingUp className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-24 mt-2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : memories.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            <Database className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No important memories found</p>
            <p className="text-xs mt-1">Try adjusting your filters or minimum score</p>
          </CardContent>
        </Card>
      ) : (
        <div>
          <div className="mb-4 text-sm text-gray-600">
            Showing {memories.length} important memories (score ≥ {filters.min_score})
          </div>
          {memories.map(renderMemory)}
        </div>
      )}
    </div>
  );
};

export default ImportantMemories;

